import React from "react";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";

export default function SecurityMeasures() {
  return (
    <LegalLayout slug="security" eyebrow="Security" title="Security Measures" lastUpdated="May 26, 2026 · Reviewed by counsel">
      <p>
        We treat the integrity of your kennel records, your payment data, and
        your account credentials as non-negotiable. This page summarises the
        technical and organisational measures we have in place.
      </p>

      <h2>Transport security</h2>
      <ul>
        <li>All traffic between your browser and our servers is encrypted with TLS 1.2 or higher.</li>
        <li>HSTS is enabled on our public domain to prevent downgrade attacks.</li>
      </ul>

      <h2>Authentication</h2>
      <ul>
        <li>Passwords are never stored in plain text. We hash with <strong>bcrypt</strong> and a per-user salt.</li>
        <li>Sessions are issued as short-lived JSON Web Tokens (JWTs), signed with a server-side secret, expiring after <strong>7 days</strong>.</li>
        <li>Optional <strong>Sign in with Google</strong> delegates identity verification to Google's OAuth 2.0 flow; we never see your Google password.</li>
        <li>
          <strong>Two-factor authentication (TOTP)</strong> is available to every user and is{" "}
          <strong>enforced on high-value actions</strong> when enabled —
          ownership transfers and escrow releases require a fresh TOTP code
          even after you're already signed in. We use the standard RFC 6238
          time-based one-time password algorithm; pair the registry with any
          authenticator app (Google Authenticator, 1Password, Authy, etc.).
        </li>
        <li>
          Public form abuse is mitigated with{" "}
          <strong>Cloudflare Turnstile</strong> in fail-closed strict mode in
          production — challenges that can't reach Cloudflare are denied,
          not silently bypassed.
        </li>
        <li>Admin access is role-gated and verified on every privileged API call.</li>
      </ul>

      <h2>Data at rest</h2>
      <ul>
        <li>Database: encrypted at rest (AES-256) on our MongoDB provider.</li>
        <li>Photo uploads: encrypted at rest in our Object Storage backend.</li>
        <li>Automated daily backups with a defined retention window.</li>
      </ul>

      <h2>Payments (PCI scope)</h2>
      <ul>
        <li>All card processing is handled by <strong>Stripe</strong>, a PCI-DSS Level 1 certified processor.</li>
        <li>We never receive, store, or transmit full card numbers, CVVs, or expiry dates. Our servers see only Stripe metadata (last-four, brand, charge ID).</li>
      </ul>

      <h2>Application-layer protections</h2>
      <ul>
        <li>Server-side validation on every endpoint — prices, permissions, and ownership are re-checked on the backend and never trusted from the client.</li>
        <li>Strict input schemas via Pydantic; typed responses prevent accidental data leakage.</li>
        <li>Permission checks on every write (dog edit, transfer approval, pedigree approval).</li>
        <li>
          <strong>Microchip uniqueness is database-enforced</strong> — the
          dogs collection has a unique partial index on the normalised
          microchip field, so the same chip cannot be claimed by two
          different registry records.
        </li>
        <li>Rate-limiting on sensitive endpoints (login, registration, password changes).</li>
        <li>Structured server logs without PII for anomaly detection.</li>
      </ul>

      <h2>Internal controls</h2>
      <ul>
        <li>Least-privilege access: only named administrators can read production data, and only via audited paths.</li>
        <li>Dependency updates tracked and deployed on a regular cadence.</li>
        <li>Code-review gate before anything reaches production.</li>
        <li>Secrets (API keys, signing keys) are stored in environment variables, never in source control.</li>
      </ul>

      <h2>Responsible disclosure</h2>
      <p>
        If you believe you have discovered a security issue — a
        vulnerability, an exposed endpoint, a data leak — please email us
        privately at <a href="mailto:security@armstrongk9registry.com">security@armstrongk9registry.com</a>
        <ContactFormLink topic="security" subject="Security disclosure" />
        {" "}<em>before</em> public disclosure. We commit to acknowledging reports
        within 3 business days and to working with good-faith researchers
        respectfully.
      </p>
      <p>
        Please do not run automated scanners, brute-force authentication
        flows, or perform testing that could impact real users. Safe-harbour
        terms apply to good-faith, responsibly-disclosed research.
      </p>

      <h2>Breach notification</h2>
      <p>
        In the unlikely event of a breach affecting your personal data, we
        will notify affected users without undue delay and in accordance with
        applicable U.S. state breach-notification laws, providing details of
        the incident, potential impact, and mitigation steps.
      </p>
    </LegalLayout>
  );
}
